import NavBar from '../components/NavBar';
import { useWeatherStore } from '../store/weatherStore';
import { isToday } from '../utils/calendar';

// Mock weather data for design previews
const mockWeather = {
  location: {
    name: "San Francisco",
    region: "CA",
    country: "United States",
    coordinates: { latitude: 37.7749, longitude: -122.4194 }
  },
  current: {
    temperature: { fahrenheit: 72, celsius: 22 },
    condition: "Sunny",
    icon: "//cdn.weatherapi.com/weather/64x64/day/113.png",
    humidity: 65,
    wind: { speed: 8, direction: "SW" },
    airQuality: { usEpaIndex: 2 }
  },
  forecast: [
    {
      date: new Date().toISOString().split('T')[0],
      high: { fahrenheit: 75, celsius: 24 },
      low: { fahrenheit: 58, celsius: 14 },
      condition: "Sunny",
      icon: "//cdn.weatherapi.com/weather/64x64/day/113.png",
      hourly: []
    },
    {
      date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      high: { fahrenheit: 73, celsius: 23 },
      low: { fahrenheit: 56, celsius: 13 },
      condition: "Partly Cloudy",
      icon: "//cdn.weatherapi.com/weather/64x64/day/116.png",
      hourly: []
    },
    {
      date: new Date(Date.now() + 172800000).toISOString().split('T')[0],
      high: { fahrenheit: 70, celsius: 21 },
      low: { fahrenheit: 54, celsius: 12 },
      condition: "Cloudy",
      icon: "//cdn.weatherapi.com/weather/64x64/day/119.png",
      hourly: []
    }
  ]
};

function getDayName(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  if (isToday(date)) return 'Today';
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

export default function WeatherDesigns() {
  const { weather: realWeather } = useWeatherStore();
  const weather = realWeather || mockWeather;
  const forecastDays = weather.forecast.slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar className="bg-white border-b border-gray-200 shadow-sm" containerClassName="px-4 py-2">
        <h1 className="text-xl font-bold text-gray-900">Mobile Weather Design Options</h1>
      </NavBar>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">8 Design Options for Mobile Weather Widget</h2>
          <p className="text-gray-600">
            All designs use {realWeather ? 'real' : 'mock'} weather data. Tap/click to see interactions.
          </p>
        </div>

        <div className="space-y-8">
          {/* Option 1: Horizontal Card Strip */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 1: Horizontal Card Strip</h3>
              <p className="text-sm text-gray-600">Apple Weather style - ~60-70px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-5xl font-light text-gray-900">{weather.current.temperature.fahrenheit}°</span>
                    <img src={weather.current.icon} alt={weather.current.condition} className="w-10 h-10" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{weather.current.condition}</div>
                      <div className="text-xs text-gray-500 truncate">{weather.location.name}, {weather.location.region}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    {forecastDays.map((day, idx) => (
                      <div key={idx} className="text-center">
                        <div className="font-semibold text-gray-900">{day.high.fahrenheit}°</div>
                        <div className="text-gray-500">{day.low.fahrenheit}°</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 2: Vertical Stack */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 2: Vertical Stack</h3>
              <p className="text-sm text-gray-600">Google Weather style - ~140-160px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-3 bg-white border-b border-gray-200">
                  <div className="text-center mb-2">
                    <img src={weather.current.icon} alt={weather.current.condition} className="w-16 h-16 mx-auto mb-1" />
                    <div className="text-6xl font-light text-gray-900 mb-1">{weather.current.temperature.fahrenheit}°</div>
                    <div className="text-base font-medium text-gray-900 mb-1">{weather.current.condition}</div>
                    <div className="text-xs text-gray-500">{weather.location.name}, {weather.location.region}</div>
                  </div>
                  <div className="space-y-1 mt-3">
                    {forecastDays.map((day, idx) => {
                      const dayName = getDayName(day.date);
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{dayName}</span>
                          <span className="font-semibold text-gray-900">{day.high.fahrenheit}°</span>
                          <span className="text-gray-500">{day.low.fahrenheit}°</span>
                          <img src={day.icon} alt={day.condition} className="w-6 h-6" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 3: Inline Compact Bar */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 3: Inline Compact Bar</h3>
              <p className="text-sm text-gray-600">Minimalist - ~50-60px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-2 bg-white border-b border-gray-200">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-4xl font-light text-gray-900">{weather.current.temperature.fahrenheit}°</span>
                    <img src={weather.current.icon} alt={weather.current.condition} className="w-7 h-7" />
                    <span className="font-medium text-gray-900">{weather.current.condition}</span>
                    <span className="text-gray-400">|</span>
                    <span className="text-xs text-gray-600">{weather.location.name}, {weather.location.region}</span>
                    <span className="text-gray-400">|</span>
                    <span className="text-xs">
                      <span className="font-semibold text-gray-900">{forecastDays[0].high.fahrenheit}°</span>
                      <span className="text-gray-500">/{forecastDays[0].low.fahrenheit}°</span>
                    </span>
                  </div>
                  <div className="flex gap-2 mt-1 text-xs">
                    {forecastDays.map((day, idx) => {
                      const dayName = getDayName(day.date);
                      return (
                        <span key={idx} className="px-2 py-1 bg-gray-100 rounded text-gray-700">
                          {dayName} {day.high.fahrenheit}°/{day.low.fahrenheit}°
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 4: Split View */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 4: Split View</h3>
              <p className="text-sm text-gray-600">Dashboard style - ~100-120px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-2 bg-white border-b border-gray-200">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-5xl font-light text-gray-900">{weather.current.temperature.fahrenheit}°</span>
                      <img src={weather.current.icon} alt={weather.current.condition} className="w-12 h-12" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{weather.current.condition}</div>
                      <div className="text-xs text-gray-500">{weather.location.name}, {weather.location.region}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    {forecastDays.map((day, idx) => {
                      const dayName = getDayName(day.date);
                      return (
                        <div key={idx}>
                          <div className="text-xs mb-1 text-gray-600">{dayName}</div>
                          <img src={day.icon} alt={day.condition} className="w-8 h-8 mx-auto mb-1" />
                          <div className="text-xs">
                            <span className="font-semibold text-gray-900">{day.high.fahrenheit}°</span>
                            <span className="text-gray-500">/{day.low.fahrenheit}°</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 5: Hero Card with Gradient */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 5: Hero Card with Gradient</h3>
              <p className="text-sm text-gray-600">Visual emphasis - ~160-180px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-4 border-b border-gray-200">
                  <div className="text-center">
                    <img src={weather.current.icon} alt={weather.current.condition} className="w-20 h-20 mx-auto mb-2" />
                    <div className="text-7xl font-light text-gray-900 mb-1">{weather.current.temperature.fahrenheit}°</div>
                    <div className="text-lg font-medium text-gray-900 mb-1">{weather.current.condition}</div>
                    <div className="text-sm text-gray-600">{weather.location.name}, {weather.location.region}</div>
                  </div>
                  <div className="flex justify-center gap-4 mt-3 text-sm">
                    {forecastDays.map((day, idx) => {
                      const dayName = getDayName(day.date);
                      return (
                        <div key={idx} className="text-center">
                          <div className="text-xs text-gray-600">{dayName}</div>
                          <div className="font-semibold text-gray-900">{day.high.fahrenheit}°</div>
                          <div className="text-gray-500 text-xs">{day.low.fahrenheit}°</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 6: Tabbed Interface */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 6: Tabbed Interface</h3>
              <p className="text-sm text-gray-600">Expandable - ~60px collapsed, ~200px expanded</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-2 bg-white border-b border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-4xl font-light text-gray-900">{weather.current.temperature.fahrenheit}°</span>
                    <img src={weather.current.icon} alt={weather.current.condition} className="w-10 h-10" />
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">{weather.current.condition}</div>
                      <div className="text-xs text-gray-500">{weather.location.name}, {weather.location.region}</div>
                    </div>
                  </div>
                  <div className="flex gap-1 border-t border-gray-200 pt-2">
                    {['Now', 'Today', '3-Day', 'Maps'].map((tab, idx) => (
                      <button
                        key={idx}
                        className={`px-3 py-1 text-xs rounded ${
                          idx === 0 ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 7: Swipeable Cards */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 7: Swipeable Cards</h3>
              <p className="text-sm text-gray-600">Carousel style - ~120-140px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-3 bg-white border-b border-gray-200">
                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded p-4 border border-blue-200">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="text-4xl font-light text-gray-900">{weather.current.temperature.fahrenheit}°</div>
                        <div className="text-sm font-medium text-gray-700">{weather.current.condition}</div>
                      </div>
                      <img src={weather.current.icon} alt={weather.current.condition} className="w-16 h-16" />
                      <div className="flex-1 text-right">
                        <div className="text-sm text-gray-600">{weather.location.name}, {weather.location.region}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          H: {forecastDays[0].high.fahrenheit}° L: {forecastDays[0].low.fahrenheit}°
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-1 mt-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Option 8: Ultra-Compact Status Bar */}
          <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Option 8: Ultra-Compact Status Bar</h3>
              <p className="text-sm text-gray-600">Maximum density - ~40-45px height</p>
            </div>
            <div className="bg-gray-100 rounded p-4">
              <div className="bg-white rounded border border-gray-200 overflow-hidden max-w-sm mx-auto">
                <div className="px-3 py-1.5 bg-white border-b border-gray-200 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-light text-gray-900">{weather.current.temperature.fahrenheit}°</span>
                    <img src={weather.current.icon} alt={weather.current.condition} className="w-5 h-5" />
                    <span className="text-gray-400">|</span>
                    <span className="text-xs text-gray-600">{weather.location.name}, {weather.location.region}</span>
                    <span className="text-gray-400">|</span>
                    <span className="text-xs">
                      H:<span className="font-semibold text-gray-900">{forecastDays[0].high.fahrenheit}°</span>{' '}
                      L:<span className="text-gray-500">{forecastDays[0].low.fahrenheit}°</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Recommendations</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• <strong>Option 1 or 3</strong> for mobile day view (maximum space efficiency)</li>
            <li>• <strong>Option 2 or 5</strong> for standalone weather page (visual emphasis)</li>
            <li>• <strong>Option 8</strong> for absolute minimum space usage</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
